import React from 'react';
import { Button, Gap, Input, Spinner } from '@salecycle/ui-component-library';
import { Container } from 'components/experimental/Container/Container';
import { Section } from 'components/experimental/Section/Section';
import { Link, useParams } from 'react-router-dom';
import { Alert2 } from 'components/experimental/Alert/Alert2';
import { ProductSendCard } from '../components/ProductSendCard';
import { NotFoundPage } from 'core/ui/components/error';
import { useFeatureFlags } from 'core/app/AppContext';
import { useTranslation } from 'react-i18next';
import { SendRulesDisabledNotice } from '../components/SendRulesDisabledNotice';
import { useSite } from 'sections/hooks/useSite';
import { emailValidCheck } from 'sections/user-management/utils/helpers';
import { useCheckCanSendMutation } from '../mutations/checkCanSendMutation';
import { StandardMainArea } from 'components/experimental/StandardMainArea';

export function TestSendRulesPage() {
  const { t } = useTranslation('compliance');
  const { siteId } = useParams<{ siteId: string }>();
  const [checkPressed, setCheckPressed] = React.useState(false);
  const [emailValid, setEmailValid] = React.useState(true);
  const [emailAddress, setEmailAddress] = React.useState('');
  const { flagEnabled } = useFeatureFlags();
  const siteQuery = useSite(siteId);

  const checkCanSendMutation = useCheckCanSendMutation(siteId, emailAddress);

  const browseCanSend = checkCanSendMutation.data?.data?.products.find(p => p.name === 'BROWSE').canSend;
  const cartCanSend = checkCanSendMutation.data?.data?.products.find(p => p.name === 'CART').canSend;
  const bulkRetargetingCanSend = checkCanSendMutation.data?.data?.products.find(p => p.name === 'BULK_RETARGETING')
    .canSend;

  const handleCheckButtonClicked = () => {
    if (!emailAddress) {
      return;
    }
    const emailIsValid = emailValidCheck(emailAddress);
    setEmailValid(emailIsValid);
    if (emailIsValid) {
      checkCanSendMutation.mutate();
      setCheckPressed(true);
    }
  };

  if (flagEnabled('compliance-send-rules')) {
    return (
      <StandardMainArea
        heading={t('testSendRulesPage.heading')}
        subHeading={t('testSendRulesPage.subHeading')}
        errorHeading={t('testSendRulesPage.errorHeading')}
        isLoading={siteQuery.isLoading}
        isError={siteQuery.isError}
      >
        <Gap top={30} />
        {siteQuery.isSuccess && siteQuery.data?.sendRules ? (
          <>
            <Section alignItems="" maxWidth="1024px">
              <Input
                type="email"
                value={emailAddress}
                onChange={v => setEmailAddress(v)}
                spacingBelow={false}
                ariaLabel={''}
                placeholder={t('testSendRulesPage.emailInputPlaceholder')}
                validationMessage={!emailValid ? t('testSendRulesPage.validEmailAddress') : ''}
              />
              <Container align="left" padding="5px 0px">
                <Button variation="secondary" onClick={handleCheckButtonClicked}>
                  Check
                </Button>
              </Container>
            </Section>
            <Gap top={30} />
            {checkPressed && checkCanSendMutation.isLoading && (
              <Container padding="50px">
                <Spinner />
              </Container>
            )}
            {checkPressed && checkCanSendMutation.isSuccess ? (
              <>
                {flagEnabled('compliance-consent-history') && (
                  <Section>
                    <span>
                      {t('testSendRulesPage.consentHistoryLink.start')}{' '}
                      <Link to={`/sites/${siteId}/compliance/consent-history?email=${emailAddress}`}>
                        {t('testSendRulesPage.consentHistoryLink.middle')}
                      </Link>{' '}
                      {t('testSendRulesPage.consentHistoryLink.end')}{' '}
                    </span>
                  </Section>
                )}
                <Gap top={20} />
                <Section columnMaxWidth="40rem">
                  <ProductSendCard
                    heading={t('testSendRulesPage.browseAbandonmentCard.heading')}
                    canSend={browseCanSend}
                    isLoading={checkCanSendMutation.isLoading}
                  />
                  <ProductSendCard
                    heading={t('testSendRulesPage.cartAbandonmentCard.heading')}
                    canSend={cartCanSend}
                    isLoading={checkCanSendMutation.isLoading}
                  />
                  <ProductSendCard
                    heading={t('testSendRulesPage.bulkRetargetingCard.heading')}
                    canSend={bulkRetargetingCanSend}
                    isLoading={checkCanSendMutation.isLoading}
                  />
                </Section>
              </>
            ) : null}
            {checkCanSendMutation.isError && (
              <Section>
                <Alert2 heading={t('testSendRulesPage.canSendCheckErrorHeading')} variation="error">
                  {t('testSendRulesPage.canSendCheckErrorMessage')}
                </Alert2>
              </Section>
            )}
            <Gap top={30} />
          </>
        ) : null}
        {siteQuery.isSuccess && !siteQuery.data?.sendRules ? <SendRulesDisabledNotice /> : null}
      </StandardMainArea>
    );
  } else {
    return <NotFoundPage />;
  }
}
